import { vec2 } from 'gl-matrix';

import { isSupportedImageMimeType } from '@canvas/constants/multimedia';
import { decodeDataUrl } from '@canvas/encoding';
import { getImageMetadata, loadImageFromFile, getImageDimensions } from '@canvas/image';
import type { MediaClient } from '@canvas/media-client';
import { type Element, type ElementData } from '@canvas/model/element';
import { isImage } from '@canvas/model/elements/image';
import { imageElementType } from '@canvas/model/elements/image';
import { type ElementClipboardData } from '@canvas/model/ui';
import { Vector2 } from '@canvas/vector';

import { getRelativeCoordinate, getSelectionCenter } from './utils';

export type ImageCopyData = ElementClipboardData & {
  fileId?: string;
  imageDataIndex: number;
  nativeSize?: Vector2 | null;
  mimeType: string;
  imageHash?: string;
  isTransparent?: boolean;
};

export type ImagePasteData = ElementClipboardData & {
  fileId?: string;
  nativeSize?: Vector2 | null;
  blob: Blob;
  mimeType: string;
  imageHash?: string;
  isTransparent: boolean;
};
export function isImageCopyData(
  element: ElementClipboardData | undefined
): element is ImageCopyData {
  return !!element && element.type === imageElementType;
}

export const getImageFromClipboardItem = async (
  item: ClipboardItem
): Promise<ImagePasteData | undefined> => {
  for (const type of item.types) {
    if (isSupportedImageMimeType(type)) {
      return imageBlobToPasteData(await item.getType(type));
    }
  }

  return undefined;
};

/**
 * Converts the given blob to a pastable image payload. Returns undefined if the blob is
 * not an image.
 */
export async function imageBlobToPasteData(
  file: Blob
): Promise<ImagePasteData | undefined> {
  if (file === null) {
    return undefined;
  }

  if (!isSupportedImageMimeType(file.type)) {
    return undefined;
  }

  const image = await loadImageFromFile(file);

  const { width, height, nativeWidth, nativeHeight } = getImageDimensions(image);
  const imageMetadata = await getImageMetadata(image);

  return {
    type: imageElementType,
    position: new Vector2(0, 0),
    size: new Vector2(width, height),
    nativeSize: new Vector2(nativeWidth, nativeHeight),
    blob: file,
    mimeType: file.type,
    isTransparent: imageMetadata?.isTransparent ?? false,
    imageHash: imageMetadata?.hash,
  };
}

/**
 * If the given element is an image, converts it to a copyable image payload. If the
 * element is not an image, returns it unchanged.
 *
 * @param element The element to process.
 * @param fileData A mutable array of image file data to be included separately in the
 *                 copy payload.
 */
export const imageDataToClipboard = (
  mediaClient: MediaClient,
  element: Element<any>,
  fileData: string[]
): ImageCopyData | ElementData | undefined => {
  if (isImage(element)) {
    // If the image has no file ID then don't copy
    if (element.fileId == null) {
      return undefined;
    }

    // If the image hasn't been fetched yet then we can't copy it
    const mediaFile = mediaClient.getImageSync(element.fileId, element.imageHash);
    if (mediaFile == null) {
      return undefined;
    }

    fileData.push(mediaFile.getDataUrl());

    return {
      id: element.id,
      type: imageElementType,
      position: element.position,
      size: element.size,
      nativeSize: element.nativeSize,
      fileId: element.fileId,
      imageDataIndex: fileData.length - 1,
      mimeType: element.mimeType,
      imageHash: element.imageHash,
      isTransparent: element.isTransparent,
    };
  }

  return element.toJSON();
};

/**
 * If the given copyable payload is an image, converts it to a pastable image payload. If
 * the copyable payload is not an image, returns it unchanged.
 *
 * @param payload The copyable image payload to convert.
 * @param getFileData A function which a file index and returns the encoded data URL for
 *                    that file.
 */
export const imageDataFromClipboard = (
  payload: ImageCopyData | ElementClipboardData,
  getFileData: (index: number) => string | undefined
): ElementClipboardData | ImagePasteData | undefined => {
  if (isImageCopyData(payload)) {
    const file = getFileData(payload.imageDataIndex);

    // File is missing so we can't paste
    if (file == null) {
      return undefined;
    }

    return {
      type: imageElementType,
      position: payload.position,
      size: payload.size,
      nativeSize: payload.nativeSize,
      fileId: payload.fileId,
      mimeType: payload.mimeType,
      blob: decodeDataUrl(file),
      imageHash: payload.imageHash,
      isTransparent: payload.isTransparent,
    };
  }

  return payload;
};

/**
 * Arranges the given array of images so that they're all positioned horizontally next to
 * each other.
 */
export const arrangeImages = (images: ImagePasteData[]) => {
  let xOffset = 0;
  for (const image of images) {
    // Offset current image by half of the current image's width
    xOffset += image.size.x / 2;

    image.position.x = xOffset;

    // Offset next image by half of the current image's width plus a gap
    xOffset += image.size.x / 2 + 20;
  }
  const center = getSelectionCenter(images);
  for (const image of images) {
    image.position = Vector2.fromJSON(
      getRelativeCoordinate(center, vec2.fromValues(image.position.x, image.position.y))
    );
  }
};
